import { useEffect, useRef, useState, useCallback } from "react";

const LANG_KEY = "sh_dictation_lang";

// Languages supported for voice typing (BCP-47 codes for the Web Speech API).
export const DICTATION_LANGS = [
  { code: "en-US", label: "English (US)" },
  { code: "en-GB", label: "English (UK)" },
  { code: "es-ES", label: "Spanish (Spain)" },
  { code: "es-US", label: "Spanish (US)" },
  { code: "es-MX", label: "Spanish (Mexico)" },
  { code: "fr-FR", label: "French" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "ar-SA", label: "Arabic" },
  { code: "hi-IN", label: "Hindi" },
  { code: "ur-PK", label: "Urdu" },
  { code: "bn-BD", label: "Bengali" },
  { code: "sw-KE", label: "Swahili" },
  { code: "ht-HT", label: "Haitian Creole" },
  { code: "de-DE", label: "German" },
  { code: "it-IT", label: "Italian" },
  { code: "zh-CN", label: "Chinese (Mandarin)" },
];

export function getDictationLang() {
  return localStorage.getItem(LANG_KEY) || "en-US";
}
export function setDictationLang(code) {
  localStorage.setItem(LANG_KEY, code);
  window.dispatchEvent(new CustomEvent("sh-dictation-lang", { detail: code }));
}
export function useDictationLang() {
  const [lang, setLang] = useState(getDictationLang());
  useEffect(() => {
    const h = (e) => setLang(e.detail || getDictationLang());
    window.addEventListener("sh-dictation-lang", h);
    return () => window.removeEventListener("sh-dictation-lang", h);
  }, []);
  return lang;
}

// Browser-native speech-to-text (Web Speech API). No key, real-time, free.
// Degrades gracefully: `supported` is false where the API is unavailable.
export function useDictation({ onFinal, lang }) {
  const Rec = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const supported = !!Rec;
  const sharedLang = useDictationLang();
  const useLang = lang || sharedLang;
  const recRef = useRef(null);
  const manualStop = useRef(false);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  const [listening, setListening] = useState(false);

  useEffect(() => {
    if (!supported) return;
    const rec = new Rec();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = useLang;
    rec.onresult = (e) => {
      let finalText = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
      }
      finalText = finalText.trim();
      if (finalText && onFinalRef.current) onFinalRef.current(finalText);
    };
    rec.onend = () => {
      if (!manualStop.current) { try { rec.start(); } catch (err) { setListening(false); } }
      else setListening(false);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed" || e.error === "audio-capture") {
        manualStop.current = true;
        setListening(false);
      }
    };
    recRef.current = rec;
    return () => { manualStop.current = true; try { rec.stop(); } catch (err) { console.debug("recognition stop skipped:", err); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, useLang]);

  const start = useCallback(() => {
    if (!supported || !recRef.current) return;
    manualStop.current = false;
    try { recRef.current.start(); setListening(true); } catch (err) { console.debug("recognition already started:", err); }
  }, [supported]);

  const stop = useCallback(() => {
    manualStop.current = true;
    try { recRef.current && recRef.current.stop(); } catch (err) { console.debug("recognition stop skipped:", err); }
    setListening(false);
  }, []);

  const toggle = useCallback(() => { listening ? stop() : start(); }, [listening, start, stop]);

  return { supported, listening, start, stop, toggle };
}
